import React, { useCallback, useEffect, useRef, useState } from "react"

export const PopUpPosition = {
  TopLeft: "topLeft",
  Top: "top",
  TopRight: "topRight",
  Bottom: "bottom",
  BottomLeft: "bottomLeft",
  BottomRight: "bottomRight",
}

/**
 * PopUp component for displaying tooltip-style overlays
 * @param {boolean} show - Controls visibility of the popup
 * @param {string} position - Position of the popup relative to its trigger (defaults to 'top')
 * @param {React.ReactNode} children - Content to display inside the popup
 * @param {string} className - Additional CSS classes to apply
 * @param {...any} props - Additional props passed to the root div element
 * @returns {JSX.Element} PopUp component
 */
export const PopUp = React.memo(
  React.forwardRef(function PopUp(
    { show, position = PopUpPosition.Top, children, className = "", ...props },
    ref
  ) {
    const positionClass = position ? ` ${position}` : ""
    const tooltipClassName = `tooltip${positionClass} ${className}`.trim()

    return (
      <div ref={ref} className={tooltipClassName} {...props}>
        <div className="tooltip-container" aria-hidden={!show}>
          <div className="tooltip-wrapper">{children}</div>
        </div>
      </div>
    )
  })
)

/**
 * HoverTooltip component that displays a tooltip on hover with configurable delay
 * @param {Object} props - Component properties
 * @param {string|JSX.Element} props.tooltip - Tooltip content to display
 * @param {boolean} [props.disabled=false] - Whether tooltip should be disabled (no hover effect)
 * @param {string} [props.position='top'] - Position of the tooltip (top, topLeft, topRight, bottom, bottomLeft, bottomRight)
 * @param {number} [props.delay=500] - Delay in milliseconds before showing tooltip on hover
 * @param {React.ReactNode} props.children - Content to wrap with tooltip functionality
 * @returns {JSX.Element} Wrapper div with hover tooltip functionality
 */
export const HoverTooltip = React.memo(function HoverTooltip({
  tooltip,
  disabled = false,
  position = PopUpPosition.Top,
  delay = 500,
  className = "",
  children,
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const timeoutRef = useRef(null)
  const tooltipId = useRef(`tooltip-${Math.random().toString(36).slice(2, 9)}`)

  const clearTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (disabled || !tooltip) return

    clearTimeout()
    timeoutRef.current = window.setTimeout(() => {
      setShowTooltip(() => true)
    }, delay)
  }, [disabled, tooltip, delay, clearTimeout])

  const handleMouseLeave = useCallback(() => {
    clearTimeout()
    setShowTooltip(() => false)
  }, [clearTimeout])

  useEffect(() => {
    return () => {
      clearTimeout()
    }
  }, [clearTimeout])

  return (
    <div
      className={`hover-tooltip ${className}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-describedby={showTooltip ? tooltipId.current : undefined}
    >
      {children}
      {showTooltip && tooltip && !disabled && (
        <PopUp
          show={showTooltip}
          position={position}
          id={tooltipId.current}
          role="tooltip"
          aria-expanded={showTooltip}
        >
          {tooltip}
        </PopUp>
      )}
    </div>
  )
})

/**
 * InfoTooltip component that displays an info icon alongside content with click-to-show tooltip
 * @param {Object} props - Component properties
 * @param {string|JSX.Element} props.tooltip - Tooltip content to display
 * @param {boolean} [props.disabled=false] - Whether tooltip should be disabled (grayed icon, no click)
 * @param {string} [props.position='topLeft'] - Position of the tooltip
 * @param {React.ReactNode} props.children - Content to display alongside the info icon
 * @returns {JSX.Element} Wrapper div with info icon and click tooltip functionality
 */
export const InfoTooltip = React.memo(function InfoTooltip({
  tooltip,
  disabled = false,
  position = PopUpPosition.TopLeft,
  children,
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipId = useRef(`info-tooltip-${Math.random().toString(36).slice(2, 9)}`)
  const tooltipRef = useRef(null)
  const iconRef = useRef(null)
  const liveRegionId = useRef(`live-${Math.random().toString(36).slice(2, 9)}`)

  const handleIconClick = useCallback(() => {
    if (disabled || !tooltip) return
    setShowTooltip(prev => !prev)
  }, [disabled, tooltip])

  const handleDismiss = useCallback((restoreFocus = false) => {
    setShowTooltip(() => false)
    if (restoreFocus && iconRef.current) {
      iconRef.current.focus()
    }
  }, [])

  const handleIconKeyDown = useCallback(
    event => {
      if (disabled || !tooltip) return

      // Handle Enter and Space keys to toggle tooltip
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault() // Prevent page scroll on Space
        setShowTooltip(prev => !prev)
      }
    },
    [disabled, tooltip]
  )

  // Handle outside clicks
  useEffect(() => {
    if (!showTooltip) return

    const handleClickOutside = event => {
      if (
        tooltipRef.current &&
        iconRef.current &&
        !tooltipRef.current.contains(event.target) &&
        !iconRef.current.contains(event.target)
      ) {
        handleDismiss()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showTooltip, handleDismiss])

  // Handle ESC key
  useEffect(() => {
    if (!showTooltip) return

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        handleDismiss(true) // Restore focus when ESC is pressed
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [showTooltip, handleDismiss])

  return (
    <div className="info-tooltip">
      {children}
      <div
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          ref={iconRef}
          className={`info-icon ${disabled ? "disabled" : ""}`}
          onClick={handleIconClick}
          onKeyDown={handleIconKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="Show more information"
          aria-expanded={showTooltip}
          aria-describedby={showTooltip ? tooltipId.current : liveRegionId.current}
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            userSelect: "none",
            outline: "none", // Remove default outline, we'll use focus-visible
          }}
        >
          ⓘ
        </span>
        {showTooltip && tooltip && !disabled && (
          <PopUp
            ref={tooltipRef}
            show={showTooltip}
            position={position}
            id={tooltipId.current}
            role="tooltip"
            aria-expanded={showTooltip}
          >
            {tooltip}
          </PopUp>
        )}
      </div>
      {/* Screen reader live region for tooltip state announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          left: "-10000px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        {showTooltip ? "Tooltip opened" : ""}
      </div>
    </div>
  )
})
